import styles from './DiscourseHistory.module.scss';
import useDocuments from '../../../Documents/Hooks/useDocuments';
import FlatList from '../../../Base/Components/FlatList';
import { DocumentItem, DocumentType } from '../../../../graphql/graphql';
import React, {
	useEffect,
	useState,
	forwardRef,
	useImperativeHandle,
} from 'react';
import DiscourseHistoryItem from './DiscourseHistoryItem';

interface Props {
	patientId: string;
	virtualPatientId: string;
	workplaceId: string;
	onShowInCalendar?: () => void;
	openCareClaimModal: (
		discourseId: string,
		discourseMainDiagnosis?: DocumentItem,
	) => void;
	onEdit: (discourseDocumentId: string) => void | Promise<void>;
}

export interface DiscourseHistoryRef {
	refetch: () => void;
}

const DiscourseHistory = forwardRef<DiscourseHistoryRef, Props>(
	(
		{
			patientId,
			workplaceId,
			onShowInCalendar,
			openCareClaimModal,
			virtualPatientId,
			onEdit,
		},
		ref,
	) => {
		const { getDocuments, getNextPage, data, isLoading, page } = useDocuments();
		const [flatData, setFlatData] = useState<any[]>([]);
		const [hasMoreData, setHasMoreData] = useState<boolean>(false);

		useEffect(() => {
			loadDiscourseHistory();
		}, [page]);

		useImperativeHandle(ref, () => ({
			refetch: loadDiscourseHistory,
		}));

		useEffect(() => {
			if (data) {
				const documentsData = data?.documents?.data;
				const totalPages = data?.documents?.meta?.totalPages;
				const page = data?.documents?.meta?.page;

				if (documentsData) {
					if (page && page > 1) {
						setFlatData(prevFlatData => [...prevFlatData, ...documentsData]);
					} else {
						setFlatData(documentsData);
					}
				}

				if (totalPages && page) {
					setHasMoreData(totalPages > page);
				}
			}
		}, [data]);

		const loadDiscourseHistory = () => {
			getDocuments(patientId, workplaceId, [DocumentType.Dekurz]);
		};

		const handleOnEdit = (id: string) => {
			onEdit(id);
		};

		return (
			<div className={styles.list} id='discourseHistoryWrapper'>
				<FlatList
					onRefresh={loadDiscourseHistory}
					onLoadMore={getNextPage}
					data={flatData}
					isLoading={isLoading}
					hasMoreData={hasMoreData}
					scrollableTargetId={'discourseHistoryWrapper'}
					renderItem={item => (
						<DiscourseHistoryItem
							patientId={patientId}
							virtualPatientId={virtualPatientId}
							item={item}
							handleEdit={handleOnEdit}
							onShowInCalendar={onShowInCalendar}
							key={item.id}
							openCareClaimModal={openCareClaimModal}
							reloadData={loadDiscourseHistory}
						/>
					)}
				/>
			</div>
		);
	},
);

DiscourseHistory.displayName = 'DiscourseHistory';

export default DiscourseHistory;
